import React, { forwardRef } from 'react';
import { StyleSheet, View, type ViewInstance } from 'react-native';

import { BodyText, Container, DisplayText, theme, useResponsive, webOnlyStyles } from '@sg/garnish';
import type { AllergenTypes } from '@sg/shared-fe-features-menu';

import { useHomeContent } from '../../hooks';
import { HomeGenericCategory } from '../HomeCategory';
import { HomeProductCard } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategories = forwardRef<ViewInstance, HomeGenericCategoriesProps>(
  (props, ref) => {
    const {
      genericCategories,
      dietaryPreferences,
      openMenu,
      openProductDetails,
      addProductToBag,
      trackItemSelection,
    } = props;

    const { currentBreakpoint, match } = useResponsive();
    const lastIndex = genericCategories.length - 1;

    // ─────────────────────────────────────────────────────────────────────────

    return (
      <Container ref={ref} style={match([styles.containerXS, styles.containerSM])}>
        {genericCategories.map((category, index) => (
          <View
            key={category.id}
            style={[
              match([styles.categoryXS, styles.categorySM]),
              match([null, index === 0 ? styles.firstCategoryPadding : null]),
            ]}
          >
            {currentBreakpoint.isXS ? (
              <BodyText style={styles.titleXS} sizeMatch={['18']}>
                {category.categoryName}
              </BodyText>
            ) : (
              <DisplayText style={styles.titleSM} sizeMatch={['40']}>
                {category.categoryName}
              </DisplayText>
            )}

            <HomeGenericCategory>
              {category.products.map((product) => (
                <HomeProductCard
                  withMaxWidth
                  key={product.lineageKey}
                  isEnriched={product.isEnriched}
                  categoryName={category.categoryName}
                  id={product.id}
                  slug={product.slug}
                  title={product.title}
                  cost={product.cost}
                  description={product.description}
                  deepLink={product.deepLink}
                  cloudinaryImage={product.cloudinaryImage}
                  contentfulImage={product.contentfulImage}
                  isModifiable={product.isModifiable}
                  dietaryPreferences={dietaryPreferences}
                  allergenProperties={product.allergenProperties}
                  calories={category.showCalories ? product.calories : 0}
                  calorieOverrideText={product.calorieOverrideText}
                  totalFatG={category.showFat ? product.totalFatG : 0}
                  totalCarbsG={category.showCarbs ? product.totalCarbsG : 0}
                  dietaryFiberG={category.showFiber ? product.dietaryFiberG : 0}
                  sugarsG={category.showSugars ? product.sugarsG : 0}
                  proteinG={category.showProtein ? product.proteinG : 0}
                  openMenu={openMenu}
                  openProductDetails={openProductDetails}
                  addProductToBag={addProductToBag}
                  trackItemSelection={trackItemSelection}
                />
              ))}
            </HomeGenericCategory>

            {index < lastIndex ? (
              <View style={match([styles.dividerXS, styles.dividerSM, dividerNegativeMargin])} />
            ) : null}
          </View>
        ))}
      </Container>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const dividerColor = 'rgba(0, 0, 0, 0.05)';
const dividerNegativeMargin = webOnlyStyles({
  height: 1,
  backgroundColor: dividerColor,
  marginTop: theme.spacing['2'],
  marginHorizontal: 'calc(100svh - 1520px)' as never,
});

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: 0,
    gap: theme.spacing['4'],
  },
  containerSM: {
    paddingHorizontal: 0,
    alignItems: 'center',
    gap: theme.spacing['10'],
  },
  titleXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  titleSM: {
    textAlign: 'center',
  },
  categoryXS: {
    gap: theme.spacing['4'],
  },
  categorySM: {
    width: '100%',
    gap: theme.spacing['8'],
  },
  firstCategoryPadding: {
    paddingTop: theme.spacing['8'],
  },
  dividerXS: {
    height: 1,
    backgroundColor: dividerColor,
  },
  dividerSM: {
    height: 1,
    backgroundColor: dividerColor,
    marginTop: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeGenericCategoriesProps = {
  genericCategories: ReturnType<typeof useHomeContent>['genericCategories'];
  dietaryPreferences: AllergenTypes;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
